import React, { useEffect, useRef } from "react";
import { useCall, CALL_STATES } from "@/context/CallContext";
import { fileUrl } from "@/lib/api";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function CallOverlay() {
  const ctx = useCall();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const localStream = ctx?.localStream;
  const remoteStream = ctx?.remoteStream;
  const state = ctx?.state;

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, state]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, state]);

  if (!ctx) return null;
  const { peer, kind, micOn, camOn, acceptCall, declineCall, endCall, toggleMic, toggleCam } = ctx;

  if (state === CALL_STATES.IDLE) return null;

  const showVideo = kind === "video" && state === CALL_STATES.ACTIVE;
  const isIncoming = state === CALL_STATES.INCOMING;
  const isOutgoing = state === CALL_STATES.OUTGOING;
  const isActive = state === CALL_STATES.ACTIVE;
  const isEnded = state === CALL_STATES.ENDED;

  return (
    <AnimatePresence>
      <motion.div
        key="call-overlay"
        className="fixed inset-0 z-[100] bg-[#0E0F12] text-[#F0E6D2] flex flex-col items-center justify-between"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="call-overlay"
      >
        {/* Remote video (full screen background) */}
        {showVideo && (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {!showVideo && (
          <audio ref={remoteVideoRef} autoPlay />
        )}

        {/* Local video (picture-in-picture) */}
        {kind === "video" && (
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="absolute top-6 right-6 w-28 h-40 object-cover rounded-2xl border-2 border-[#F0E6D2]/35 shadow-2xl z-10"
          />
        )}

        {/* Header: peer info + status */}
        <div className="relative z-10 w-full pt-14 px-8 flex flex-col items-center text-center">
          <div className="w-28 h-28 rounded-full overflow-hidden bg-[#1B1B1E]/10 mb-4 ring-4 ring-[#F0E6D2]/25">
            {peer?.photo ? (
              <img loading="lazy" decoding="async" src={fileUrl(peer.photo, 640)} alt={peer?.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display text-5xl font-black">
                {peer?.name?.[0]}
              </div>
            )}
          </div>
          <h1 className="font-display text-4xl font-light tracking-tight mb-1" data-testid="call-peer-name">
            {peer?.name || "Unknown"}
          </h1>
          <p className="text-xs uppercase font-bold tracking-widest text-[#F0E6D2]/70" data-testid="call-status">
            {isIncoming && (kind === "video" ? "Incoming video call" : "Incoming voice call")}
            {isOutgoing && "Calling…"}
            {isActive && "Connected"}
            {isEnded && "Call ended"}
          </p>
        </div>

        {/* Controls */}
        <div className="relative z-10 w-full pb-12 px-8">
          {isIncoming && (
            <div className="flex items-center justify-around max-w-xs mx-auto">
              <button
                onClick={() => declineCall("declined")}
                className="w-16 h-16 rounded-full bg-[#B95F73] flex items-center justify-center active:scale-90"
                data-testid="call-decline-btn"
              >
                <PhoneOff className="w-7 h-7" />
              </button>
              <button
                onClick={acceptCall}
                className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center active:scale-90"
                data-testid="call-accept-btn"
              >
                <Phone className="w-7 h-7" />
              </button>
            </div>
          )}
          {isOutgoing && (
            <div className="flex items-center justify-center">
              <button
                onClick={endCall}
                className="w-16 h-16 rounded-full bg-[#B95F73] flex items-center justify-center active:scale-90"
                data-testid="call-cancel-btn"
              >
                <PhoneOff className="w-7 h-7" />
              </button>
            </div>
          )}
          {isActive && (
            <div className="flex items-center justify-around max-w-sm mx-auto">
              <button
                onClick={toggleMic}
                className={`w-14 h-14 rounded-full flex items-center justify-center active:scale-90 ${
                  micOn ? "bg-[#1B1B1E]/10" : "bg-[#1B1B1E] text-[#F0E6D2]"
                }`}
                data-testid="call-toggle-mic"
              >
                {micOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
              </button>
              {kind === "video" && (
                <button
                  onClick={toggleCam}
                  className={`w-14 h-14 rounded-full flex items-center justify-center active:scale-90 ${
                    camOn ? "bg-[#1B1B1E]/10" : "bg-[#1B1B1E] text-[#F0E6D2]"
                  }`}
                  data-testid="call-toggle-cam"
                >
                  {camOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                </button>
              )}
              <button
                onClick={endCall}
                className="w-16 h-16 rounded-full bg-[#B95F73] flex items-center justify-center active:scale-90"
                data-testid="call-end-btn"
              >
                <PhoneOff className="w-7 h-7" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
